<template>
	<div class="base-input" :class="{ 'base-input--has-error': hasError }">
		<label v-if="label" class="base-input__label" :for="inputId">
			{{ label }}
			<span v-if="required" class="base-input__required">*</span>
		</label>

		<input
			:id="inputId"
			:type="type"
			:value="modelValue"
			:placeholder="placeholder"
			:required="required"
			:disabled="disabled"
			:readonly="readonly"
			:step="step"
			:min="min"
			:max="max"
			class="base-input__field"
			@input="handleInput"
			@blur="handleBlur"
			@focus="handleFocus"
		/>

		<div v-if="error" class="base-input__error">
			{{ error }}
		</div>
	</div>
</template>

<script lang="ts">
import { computed, defineComponent } from 'vue';

let inputIdCounter = 0;

export default defineComponent({
	name: 'BaseInput',

	props: {
		modelValue: {
			type: [String, Number],
			default: '',
		},
		type: {
			type: String,
			default: 'text',
		},
		label: {
			type: String,
			default: '',
		},
		placeholder: {
			type: String,
			default: '',
		},
		required: {
			type: Boolean,
			default: false,
		},
		disabled: {
			type: Boolean,
			default: false,
		},
		readonly: {
			type: Boolean,
			default: false,
		},
		error: {
			type: String,
			default: '',
		},
		step: {
			type: [String, Number],
			default: undefined,
		},
		min: {
			type: [String, Number],
			default: undefined,
		},
		max: {
			type: [String, Number],
			default: undefined,
		},
	},

	emits: ['update:modelValue', 'blur', 'focus'],

	setup(props, { emit }) {
		const inputId = `base-input-${++inputIdCounter}`;

		const hasError = computed(() => !!props.error);

		const handleInput = (event: Event) => {
			const target = event.target as HTMLInputElement;
			let value: string | number = target.value;

			if (props.type === 'number' && value !== '') {
				value = Number(value);
			}

			emit('update:modelValue', value);
		};

		const handleBlur = (event: Event) => {
			emit('blur', event);
		};

		const handleFocus = (event: Event) => {
			emit('focus', event);
		};

		return {
			inputId,
			hasError,
			handleInput,
			handleBlur,
			handleFocus,
		};
	},
});
</script>

<style scoped lang="scss">
.base-input {
	display: flex;
	flex-direction: column;
	margin-bottom: 1rem;

	&__label {
		margin-bottom: 0.5rem;
		color: #374151;
		font-size: 1rem;
		font-weight: 600;
	}

	&__required {
		color: #ef4444;
	}

	&__field {
		padding: 0.875rem;
		font-size: 1rem;
		transition: border-color 0.2s ease, box-shadow 0.2s ease;
		border: 1px solid #d1d5db;
		border-radius: 8px;

		&:focus {
			border-color: #3b82f6;
			outline: none;
			box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
		}

		&:disabled {
			color: #6b7280;
			cursor: not-allowed;
			background-color: #f9fafb;
		}

		&::placeholder {
			color: #9ca3af;
		}
	}

	&__error {
		margin-top: 0.25rem;
		color: #ef4444;
		font-size: 0.75rem;
	}

	&--has-error {
		.base-input__field {
			border-color: #ef4444;

			&:focus {
				border-color: #ef4444;
				box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1);
			}
		}
	}
}
</style>
